import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Alert,
  ProgressBar,
} from 'react-bootstrap';
import {
  People,
  Person,
  Hospital,
  Cash,
  GraphUp,
  Shield,
  InfoCircle,
  FileText,
  CheckCircle,
  XCircle,
  Calendar,
  Award,
  Clock,
  PersonCircle,
  Activity,
  Download,
  Printer,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données fictives (inchangées)
const mockStats = {
  totalPatients: 1234,
  totalDoctors: 45,
  totalPharmacies: 23,
  totalReimbursements: 2500000,
  pendingReimbursements: 18,
  fraudAlerts: 3,
  satisfactionRate: 92,
  monthlyGrowth: 12,
};

const mockRecentReimbursements = [
  { id: 'R001', date: '2025-03-12', patient: 'Jean Dupont', amount: 15000, status: 'pending' },
  { id: 'R002', date: '2025-03-11', patient: 'Marie Nzogho', amount: 8000, status: 'pending' },
  { id: 'R003', date: '2025-03-10', patient: 'Pierre Mba', amount: 12000, status: 'approved' },
  { id: 'R004', date: '2025-03-09', patient: 'Sophie Nguema', amount: 5000, status: 'pending' },
  { id: 'R005', date: '2025-03-08', patient: 'Paul Obiang', amount: 9500, status: 'rejected' },
];

const mockFraudAlerts = [
  { id: 'F001', date: '2025-03-12', type: 'Prescription suspecte', description: 'Plusieurs prescriptions identiques', severity: 'medium', status: 'new' },
  { id: 'F002', date: '2025-03-10', type: 'Double remboursement', description: 'Demande en double', severity: 'high', status: 'investigating' },
  { id: 'F003', date: '2025-03-08', type: 'Faux document', description: 'Document falsifié', severity: 'high', status: 'resolved' },
];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [expandedReimbursements, setExpandedReimbursements] = useState(new Set());
  const [expandedFrauds, setExpandedFrauds] = useState(new Set());

  const toggleReimbursement = (id) => {
    const newSet = new Set(expandedReimbursements);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedReimbursements(newSet);
  };

  const toggleFraud = (id) => {
    const newSet = new Set(expandedFrauds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedFrauds(newSet);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge bg="warning" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> En attente</Badge>,
      approved: <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Approuvé</Badge>,
      rejected: <Badge bg="danger" className="rounded-pill px-3 py-2"><XCircle size={12} className="me-1" /> Rejeté</Badge>,
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const getSeverityBadge = (severity) => {
    const colors = { low: 'success', medium: 'warning', high: 'danger' };
    return <Badge bg={colors[severity]} className="rounded-pill px-3 py-2">{severity}</Badge>;
  };

  const getAlertStatusBadge = (status) => {
    const badges = {
      new: <Badge bg="danger" className="rounded-pill px-3 py-2">Nouveau</Badge>,
      investigating: <Badge bg="warning" className="rounded-pill px-3 py-2">En cours</Badge>,
      resolved: <Badge bg="success" className="rounded-pill px-3 py-2">Résolu</Badge>,
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const statsCards = [
    {
      title: 'Assurés',
      value: mockStats.totalPatients.toLocaleString(),
      icon: People,
      color: '#4361ee',
      gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)',
      bg: 'white',
      trend: '+12%',
    },
    {
      title: 'Médecins',
      value: mockStats.totalDoctors,
      icon: Person,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      bg: 'white',
      trend: '+5%',
    },
    {
      title: 'Pharmacies',
      value: mockStats.totalPharmacies,
      icon: Hospital,
      color: '#06b6d4',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      bg: 'white',
      trend: '+8%',
    },
    {
      title: 'Remboursements',
      value: formatCurrency(mockStats.totalReimbursements),
      icon: Cash,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bg: 'white',
      trend: '+18%',
    },
  ];

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête avec bienvenue */}
        <div className="welcome-section mb-4 p-4 rounded-4" style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
        }}>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-circle p-3">
                  <PersonCircle size={48} style={{ color: '#1e3c72' }} />
                </div>
                <div>
                  <h1 className="h3 fw-bold mb-1">Bonjour, {user?.name?.split(' ')[0] || 'Administrateur'} !</h1>
                  <p className="mb-0 opacity-90">Voici le résumé de l'activité de la plateforme.</p>
                  
                </div>
              
              </div>
                <div className="mt-2">
                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                      <Activity className="me-1" size={14} />
                      3 nouvelles inscriptions aujourd'hui
                    </Badge>
                  </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button variant="light" className="rounded-pill px-4 me-2"> 
                <Download size={16} className="me-2" /> Exporter
              </Button>
              <Button variant="outline-light" className="rounded-pill px-4">
                <Printer size={16} className="me-2" /> Imprimer
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h4 className="mb-0 fw-bold" style={{ fontSize: '1.2rem' }}>
                        {card.value}
                      </h4>
                      <div className="mt-1">
                        <Badge bg="light" text="dark" className="rounded-pill px-2 py-1 small">
                          <GraphUp size={10} className="me-1" />
                          {card.trend}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Section Remboursements récents */}
        <Row className="g-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Cash size={18} className="text-success" />
                  Remboursements récents
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Patient</th>
                        <th className="d-none d-sm-table-cell">Montant</th>
                        <th className="d-none d-sm-table-cell">Statut</th>
                        <th className="d-sm-none">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockRecentReimbursements.slice(0, 5).map(r => {
                        const isExpanded = expandedReimbursements.has(r.id);
                        return (
                          <React.Fragment key={r.id}>
                            <tr>
                              <td data-label="Date" className="small">{formatDate(r.date)}</td>
                              <td data-label="Patient" className="fw-medium">{r.patient}</td>
                              <td data-label="Montant" className="fw-bold text-success d-none d-sm-table-cell">{formatCurrency(r.amount)}</td>
                              <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(r.status)}</td>
                              <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                                <Button
                                  variant="link"
                                  onClick={() => toggleReimbursement(r.id)}
                                  className="p-0 text-secondary"
                                  style={{ textDecoration: 'none' }}
                                >
                                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </Button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="d-sm-none detail-row">
                                <td colSpan="5" className="p-3 bg-light">
                                  <div className="row g-2">
                                    <div className="col-6"><strong>Montant :</strong> {formatCurrency(r.amount)}</div>
                                    <div className="col-6"><strong>Statut :</strong> {getStatusBadge(r.status)}</div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0 pt-3 pb-4">
                <Button variant="outline-primary" size="sm" className="rounded-pill w-100">
                  Voir tous les remboursements
                </Button>
              </Card.Footer>
            </Card>
          </Col>

          {/* Section Alertes fraude */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Shield size={18} className="text-danger" />
                  Alertes fraude
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th className="d-none d-sm-table-cell">Niveau</th>
                        <th className="d-none d-sm-table-cell">Statut</th>
                        <th className="d-sm-none">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockFraudAlerts.map(a => {
                        const isExpanded = expandedFrauds.has(a.id);
                        return (
                          <React.Fragment key={a.id}>
                            <tr>
                              <td data-label="Date" className="small">{formatDate(a.date)}</td>
                              <td data-label="Type" className="fw-medium">{a.type}<br /><small className="text-muted">{a.description}</small></td>
                              <td data-label="Niveau" className="d-none d-sm-table-cell">{getSeverityBadge(a.severity)}</td>
                              <td data-label="Statut" className="d-none d-sm-table-cell">{getAlertStatusBadge(a.status)}</td>
                              <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                                <Button
                                  variant="link"
                                  onClick={() => toggleFraud(a.id)}
                                  className="p-0 text-secondary"
                                  style={{ textDecoration: 'none' }}
                                >
                                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </Button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="d-sm-none detail-row">
                                <td colSpan="5" className="p-3 bg-light">
                                  <div className="row g-2">
                                    <div className="col-6"><strong>Niveau :</strong> {getSeverityBadge(a.severity)}</div>
                                    <div className="col-6"><strong>Statut :</strong> {getAlertStatusBadge(a.status)}</div>
                                    <div className="col-12"><strong>Description :</strong> {a.description}</div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0 pt-3 pb-4">
                <Button variant="outline-danger" size="sm" className="rounded-pill w-100">
                  Gérer les alertes
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        {/* Informations supplémentaires */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Tableau de bord administrateur</strong>
                  <span className="small">
                    Ces données sont mises à jour en temps réel. Utilisez les menus pour gérer les utilisateurs, les structures et les remboursements.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 767.98px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AdminDashboard;